import get from 'lodash/get';
import { DeviationTypes, DeviationResourceType, DeviationSuggestionReasons, } from '../types/deviation';
import { DEVIATION_PATHS } from '@wix/da-url/dist/src/constants';
import { Url } from '@wix/da-url';
import { getFeature } from '../DaEditorWrapper/Features/helper';
import { MEDIA_COUNT_FEATURE } from '../DaEditorWrapper/Features/mediaCount';
import DeviationMediaService from '../utils/DeviationMediaService';
var CHARS_BEFORE_LONG_STATUS = 110;
export var JOURNAL_CATEGORY_PREFIXES = ['journals', 'personaljournal'];
// for fullviews/art stage
var TINY_IMAGE_THRESHOLD_WIDTH = 400;
var TINY_IMAGE_THRESHOLD_HEIGHT = 400;
var TINY_IMAGE_THRESHOLD_WIDTH_MOBILE = 200;
var TINY_IMAGE_THRESHOLD_HEIGHT_MOBILE = 180;
export function styleFromWidthHeight(_a, responsive) {
    var width = _a.width, height = _a.height;
    if (responsive === void 0) { responsive = true; }
    // if nothing is set, always fill container
    if (!width && !height) {
        return {
            width: '100%',
            height: '100%',
        };
    }
    // if responsive mode, or both props are set - just use whatever is set
    if (responsive || (width && height)) {
        return { width: width, height: height };
    }
    // otherwise try to square it off
    return {
        width: width || height || '100%',
        height: height || width || '100%',
    };
}
export function getDeviationType(deviation) {
    return DeviationMediaService.getDeviationType(deviation);
}
export function getDeviationResourceType(deviation) {
    var type = getDeviationType(deviation);
    switch (type) {
        case DeviationTypes.JOURNAL:
            return DeviationResourceType.JOURNAL;
        case DeviationTypes.POLL:
            return DeviationResourceType.POLL;
        case DeviationTypes.COMMISSION:
            return DeviationResourceType.COMMISSION;
        case DeviationTypes.STATUS:
            return DeviationResourceType.STATUS;
        default:
            return DeviationResourceType.ART;
    }
}
export function isDeviationAuthor(deviation, user) {
    var authorUserId = get(deviation, 'extended.originalAuthor.userId', get(deviation, 'author.userId', false));
    return authorUserId === user.userId;
}
export function isDeviationPixelArt(deviation) {
    return false; // we don't support pixel detection for now
}
export function isGifDeviation(deviation) {
    return DeviationMediaService.isGif(deviation);
}
export function isDeviationTransparent(deviation) {
    // get our representative fullview file
    var fullview = DeviationMediaService.getLargestFullview(deviation);
    // and see if it's transparent
    return !!fullview.transparency;
}
export function isDeviationTextEditable(deviation) {
    var isTextEditable = !!deviation.isTextEditable;
    var hasLegacyTextEditUrl = !!deviation.legacyTextEditUrl;
    switch (getDeviationType(deviation)) {
        case DeviationTypes.LITERATURE:
        case DeviationTypes.JOURNAL:
            return isTextEditable || hasLegacyTextEditUrl;
        case DeviationTypes.COMMISSION:
        case DeviationTypes.POLL:
        case DeviationTypes.STATUS:
            return isTextEditable;
        default:
            return false;
    }
}
export var isImageDeviation = function (deviation) {
    return getDeviationType(deviation) === DeviationTypes.IMAGE;
};
export function getDeviationBasePath(deviation) {
    switch (getDeviationType(deviation)) {
        case DeviationTypes.JOURNAL:
            return DEVIATION_PATHS.JOURNAL;
        case DeviationTypes.COMMISSION:
            return DEVIATION_PATHS.COMMISSION;
        case DeviationTypes.POLL:
            return DEVIATION_PATHS.POLL;
        case DeviationTypes.STATUS:
            return DEVIATION_PATHS.STATUS;
        default:
            return DEVIATION_PATHS.DEVIATION;
    }
}
// Do NOT override useUrl unless you are really really sure you know what you're doing
export function getDeviationUrl(deviation, useUrl) {
    if (useUrl === void 0) { useUrl = true; }
    var deviationId = deviation.deviationId, deviationTitle = deviation.title, author = deviation.author, url = deviation.url;
    // on dev, we always want to use generated urls because otherwise
    // devpage inner links will lead to green
    if (!useUrl || !url || process.env.NODE_ENV !== 'production') {
        return Url.deviationLink(deviationId, author && author.username, deviationTitle, getDeviationBasePath(deviation));
    }
    return url;
}
export function getDeviationPath(deviation) {
    var deviationId = deviation.deviationId, deviationTitle = deviation.title, author = deviation.author;
    return Url.deviationPath(deviationId, author && author.username.toLowerCase(), deviationTitle, getDeviationBasePath(deviation));
}
export function isValidFileSrc(src) {
    return (/^https:\/\/(.+)\.deviantart\.net/.test(src) ||
        /^https:\/\/images-wixmp-ed30a86b8c4ca887773594c2/.test(src) ||
        /^https:\/\/images-wixmp-395331243bc2089e8c09c8d3/.test(src));
}
export function getFilePixelSize(file) {
    return (file.width || 0) * (file.height || 0);
}
export function isFileLargerThanSize(file, width, height) {
    if (width && height && file.width && file.height) {
        return file.width > width || file.height > height;
    }
    else if (width && file.width) {
        return file.width > width;
    }
    else if (height && file.height) {
        return file.height > height;
    }
    return false;
}
export function isFileLargerThanSizeOrEqual(file, width, height) {
    if (width && height && file.width && file.height) {
        return file.width >= width || file.height >= height;
    }
    else if (width && file.width) {
        return file.width >= width;
    }
    else if (height && file.height) {
        return file.height >= height;
    }
    return false;
}
export function isFileTiny(file, isMobile) {
    if (!file.width || !file.height) {
        return false;
    }
    var thresholdWidth = isMobile
        ? TINY_IMAGE_THRESHOLD_WIDTH_MOBILE
        : TINY_IMAGE_THRESHOLD_WIDTH;
    var thresholdHeight = isMobile
        ? TINY_IMAGE_THRESHOLD_HEIGHT_MOBILE
        : TINY_IMAGE_THRESHOLD_HEIGHT;
    return file.width < thresholdWidth && file.height < thresholdHeight;
}
export function scaleToSize(content, container) {
    // if anything is missing, bail
    if (!content.width ||
        !content.height ||
        !container.width ||
        !container.height) {
        return;
    }
    // if our content is small enough, noop
    if (content.width <= container.width && content.height <= container.height) {
        return { width: content.width, height: content.height };
    }
    // we need to check along which axis do we need to scale more (ie. smaller scale factor)
    var widthScale = container.width / content.width;
    var heightScale = container.height / content.height;
    if (widthScale < heightScale) {
        return {
            width: container.width,
            height: content.height * widthScale,
        };
    }
    return {
        width: content.width * heightScale,
        height: container.height,
    };
}
export function getIsShortStatus(_a) {
    var textContent = _a.textContent;
    if (!textContent) {
        return false;
    }
    var isLegacy = textContent.html.type === 'writer';
    var hasMedia = false;
    if (!isLegacy) {
        var mediaCounts = getFeature(MEDIA_COUNT_FEATURE, textContent.html.features);
        if (mediaCounts &&
            (mediaCounts.data.images > 0 || mediaCounts.data.video > 0)) {
            hasMedia = true;
        }
    }
    return textContent.excerpt.length < CHARS_BEFORE_LONG_STATUS && !hasMedia;
}
export function getTranslatedDeviationTitle(deviation, t) {
    var author = deviation.author, title = deviation.title;
    if (!author) {
        return title;
    }
    if (getDeviationType(deviation) === DeviationTypes.STATUS) {
        return t('deviation.deviationTitle.status', { author: author.username });
    }
    return t('deviation.deviationTitle', { title: title, author: author.username });
}
export function getIsTextType(deviation) {
    return [
        DeviationTypes.LITERATURE,
        DeviationTypes.JOURNAL,
        DeviationTypes.STATUS,
        DeviationTypes.POLL,
    ].includes(getDeviationType(deviation));
}
export function getIsStreamTextType(deviations) {
    return deviations.every(function (deviation) { return getIsTextType(deviation); });
}
export function deviationTypeToTranslationKey(type) {
    var _a;
    var typeMap = (_a = {},
        _a[DeviationTypes.COMMISSION] = 'commission',
        _a[DeviationTypes.POLL] = 'poll',
        _a[DeviationTypes.STATUS] = 'status',
        _a[DeviationTypes.JOURNAL] = 'journal',
        _a);
    return typeMap.hasOwnProperty(type)
        ? "common." + typeMap[type]
        : "common.deviation";
}
export function getSource(media) {
    if (!media || media.length === 0) {
        return null;
    }
    // TODO: pick the size that fits the screen size the best
    // get the largest quality available for now
    var maxQuality = media
        .map(function (file) { return parseInt("" + file.quality, 10); })
        .sort(function (a, b) { return b - a; })[0];
    return media.find(function (file) {
        return file.quality ? file.quality.startsWith("" + maxQuality) : false;
    });
}
export function getPremiumFolderProperties(deviation, hasPremiumFeature) {
    if (hasPremiumFeature === void 0) { hasPremiumFeature = false; }
    // make-believe premium folders
    // TODO: fix all of this when backend ready
    var isPremiumOnly = hasPremiumFeature && deviation.isPurchasable;
    return {
        isPremium: isPremiumOnly,
        isPremiumLocked: isPremiumOnly && !deviation.isFavourited,
        isPaidFolder: isPremiumOnly && !!deviation.isMature,
        pointPrice: 200,
        dollarPrice: 3,
    };
}
export function getBlurredThumbStyles(deviation) {
    // TODO: use actual blurred thumb image, this is fake
    var thumb = DeviationMediaService.getSmallestThumb(deviation);
    if (!thumb) {
        return;
    }
    return {
        backgroundImage: "url('" + thumb.src + "')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'hidden',
        filter: 'blur(25px)',
    };
}
export function getSuggestionReasonsByPriority(deviation) {
    var priority = [
        DeviationSuggestionReasons.DailyDeviation,
        DeviationSuggestionReasons.WatchingUser,
        DeviationSuggestionReasons.WatchingGroup,
        DeviationSuggestionReasons.WatchingTopic,
        DeviationSuggestionReasons.Recommended,
        DeviationSuggestionReasons.NewDeviant,
        DeviationSuggestionReasons.Popular,
    ];
    // just filter
    return priority.filter(function (reason) { var _a; return (_a = deviation.suggestedReasons) === null || _a === void 0 ? void 0 : _a.includes(reason); });
}
//# sourceMappingURL=_helpers.js.map